import React from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import ReactTable from "../../../../../components/common/ReactTable";
import Spinner from "../../../../../components/common/Spinner";

const AssignmentsTab = ({
  mode,
  canViewAssignments,
  canEditAssignments,
  canUpdateCommitteeMemberRole,
  canUpdateCommitteeMemberDetails,
  canFilterCommitteeMembers,
  canSearchCommitteeMembers,
  canRemoveCommitteeAssignment,
  authorityType,
  setAuthorityType,
  authorityOpen,
  setAuthorityOpen,
  clearCommitteeAssignments,
  clearChapterAssignments,
  clearProgramAssignments,
  committeeRows,
  handleCouncilChange,
  handleCommitteeStateChange,
  handleCommitteeRegionChange,
  handleCommitteeDistrictChange,
  handleCommitteeSubtypeChange,
  handleCommitteeChange,
  handleDesignationChange,
  handleScopeChange,
  emptyScope,
  scopeLevels,
  councils,
  states,
  regions,
  districts,
  chapters,
  programs,
  scopeRegionsByState,
  scopeDistrictsByState,
  handleRemoveCommittee,
  handleAddCommitteeRow,
  handleAddChapterAssignments,
  userExistingChapterAssignments,
  userAssignments,
  existingAssignmentColumns,
  existingAssignmentRows,
  addChapterAssignments,
  chapterRows,
  programRows,
  programBaskets,
  handleAddProgramRow,
  handleRemoveProgramRow,
  handleProgramBasketChange,
  handleProgramSelectionChange,
  handleProgramScopeChange,
  handleChapterScopeChange,
  testColumns,
  loading,
}) => {
  if (!canViewAssignments) return null;

  const isEditable = mode !== "view" && canEditAssignments;
  const canEditCommitteeDetails =
    isEditable && canUpdateCommitteeMemberDetails;
  const canEditCommitteeRole =
    isEditable && canUpdateCommitteeMemberRole;
  const canUseCommitteeFilters =
    isEditable && canFilterCommitteeMembers;
  const canUseCommitteeSearch =
    isEditable && canSearchCommitteeMembers;
  const hasCommitteeAssignments = committeeRows.some((row) => row.committee && (row.designations || []).length);
  const hasChapterAssignments = (userExistingChapterAssignments || []).length > 0 || chapterRows.some((row) => row.isAssigned);
  const hasProgramAssignments = programRows.some((row) => row.basketId);
  const hasRbacAssignments = Array.isArray(userAssignments) && userAssignments.length > 0;
  const showRbacWarning = mode === "view" && !hasRbacAssignments && (hasCommitteeAssignments || hasChapterAssignments || hasProgramAssignments);
  const viewModeReadabilitySx =
    mode === "view"
      ? {
          "& .MuiInputBase-root.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.87)",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiSelect-select.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.87)",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiSvgIcon-root.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.54)",
          },
          "& .MuiChip-root.Mui-disabled": {
            opacity: 1,
          },
          "& .MuiChip-root.Mui-disabled .MuiChip-label": {
            color: "rgba(0, 0, 0, 0.87)",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
          },
        }
      : undefined;
  console.log("committeeRows", committeeRows);

  const getOptionId = (item) => {
    if (!item) return "";
    if (typeof item === "object") return item._id || item.id || item.$oid || "";
    return item;
  };

  const getOptionLabel = (item, keys) => {
    if (!item) return "";
    if (typeof item !== "object") return String(item);
    for (const key of keys) {
      const value = item?.[key];
      if (!value) continue;
      if (typeof value === "object") {
        const nestedLabel = getOptionLabel(value, keys);
        if (nestedLabel) return nestedLabel;
        continue;
      }
      return String(value);
    }
    return String(item.name || item.label || getOptionId(item) || "");
  };

  const getStateLabel = (state) => {
    const id = String(getOptionId(state) || "");
    const label = getOptionLabel(state, ["stateName", "name", "label"]);
    const masterState = (states || []).find(
      (item) => String(getOptionId(item) || "") === id || String(getOptionId(item) || "") === label,
    );

    if (masterState) {
      return getOptionLabel(masterState, ["stateName", "name", "label"]);
    }

    return label;
  };
  const getRegionLabel = (region) =>
    getOptionLabel(region, ["regionName", "name", "label"]);
  const getDistrictLabel = (district) =>
    getOptionLabel(district, ["districtName", "name", "label"]);

  const getCommitteeEmptyMessage = (row) => {
    if (!row?.council) return "Select council first";
    if (row.councilType === "SEC" && !row.committeeState) {
      return "Select state to view committees";
    }
    if (row.councilType === "REC" && !row.committeeRegion) {
      return "Select region to view committees";
    }
    if (row.councilType === "DEC" && !row.committeeDistrict) {
      return "Select district to view committees";
    }
    return "No committee found for selected scope";
  };
  return (
    <Box sx={viewModeReadabilitySx}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mt: 5,
          mb: 2,
          pb: 1,
          borderBottom: "2px solid #eee",
        }}
      >
        User Assignment
      </Typography>
      {showRbacWarning && (
        <Box
          sx={{
            my: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: "#fff3e0",
            border: "1px solid #ffcc80",
          }}
        >
          <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 600 }}>
            RBAC assignments missing
          </Typography>
          <Typography variant="body2" sx={{ color: "#6d4c41" }}>
            Committee/Chapter/Program assignments are visible from their own tables,
            but no UserAssignment records exist for this user. Permissions will not apply
            until UserAssignment is seeded.
          </Typography>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Assign Authority For</InputLabel>
            <Select
              multiple
              open={authorityOpen}
              onOpen={() => setAuthorityOpen(true)}
              onClose={() => setAuthorityOpen(false)}
              value={authorityType}
              input={<OutlinedInput label="Assign Authority For" />}
              onChange={(e) => {
                if (!isEditable) return;
                const next = e.target.value;
                const nextValues = Array.isArray(next) ? next : [next];
                setAuthorityType(nextValues);
                if (!nextValues.includes("COMMITTEE")) {
                  clearCommitteeAssignments();
                }
                if (!nextValues.includes("CHAPTER")) {
                  clearChapterAssignments();
                }
                if (!nextValues.includes("PROGRAM")) {
                  clearProgramAssignments();
                }
                setAuthorityOpen(false);
              }}
              disabled={!isEditable || !canEditCommitteeDetails}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      size="small"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onDelete={
                        mode === "view"
                          ? undefined
                          : (e) => {
                              e.stopPropagation();
                              setAuthorityType((prev) => {
                                const nextValues = prev.filter(
                                  (item) => item !== value,
                                );
                                if (!nextValues.includes("COMMITTEE")) {
                                  clearCommitteeAssignments();
                                }
                                if (!nextValues.includes("CHAPTER")) {
                                  clearChapterAssignments();
                                }
                                if (!nextValues.includes("PROGRAM")) {
                                  clearProgramAssignments();
                                }
                                return nextValues;
                              });
                            }
                      }
                    />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="COMMITTEE">Committee</MenuItem>
              <MenuItem value="CHAPTER">Chapter</MenuItem>
              <MenuItem value="PROGRAM">Program</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {committeeRows.map((row, index) => (
        <Box
          key={index}
          sx={{
            mt: 1.5,
            py: 1,
            px: 1.5,
            border: "1px solid #ececec",
            borderRadius: 1,
          }}
        >
          {authorityType.includes("COMMITTEE") && (
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Committee Assignment {index + 1}
              </Typography>
              {mode !== "view" &&
                canEditCommitteeDetails &&
                canRemoveCommitteeAssignment && (
                <Button
                  color="error"
                  variant="text"
                  size="small"
                  disabled={!isEditable}
                  onClick={() => handleRemoveCommittee(index)}
                >
                  Remove
                </Button>
              )}
            </Box>
          )}
          <Grid container spacing={2}>
            {authorityType.includes("COMMITTEE") && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Council</InputLabel>
                    <Select
                      label="Council"
                      value={row.council}
                      disabled={!canUseCommitteeFilters}
                      onChange={(e) =>
                        handleCouncilChange(index, e.target.value)
                      }
                    >
                      {councils.map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.councilName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {(row.committeeStates?.length > 0 ||
                  ["SEC", "REC", "DEC"].includes(row.councilType)) && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" disabled={!row.council}>
                      <InputLabel>State</InputLabel>
                      <Select
                        label="State"
                        value={row.committeeState || ""}
                        disabled={!canUseCommitteeFilters}
                        onChange={(e) =>
                          handleCommitteeStateChange(index, e.target.value)
                        }
                      >
                        {(["REC", "DEC"].includes(row.councilType) ||
                        (mode === "create" && row.councilType === "SEC")
                          ? states
                          : row.committeeStates
                        ).map((s) => (
                          <MenuItem key={getOptionId(s)} value={getOptionId(s)}>
                            {getStateLabel(s)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {row.councilType === "REC" || row.councilType === "DEC" ? (
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!row.committeeState}
                    >
                      <InputLabel>Region</InputLabel>
                      <Select
                        label="Region"
                        value={row.committeeRegion || ""}
                        disabled={!canUseCommitteeFilters}
                        onChange={(e) =>
                          handleCommitteeRegionChange(index, e.target.value)
                        }
                      >
                        {(row.committeeRegions || []).map((r) => (
                          <MenuItem key={getOptionId(r)} value={getOptionId(r)}>
                            {getRegionLabel(r)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}

                {row.councilType === "DEC" ? (
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!row.committeeRegion}
                    >
                      <InputLabel>District</InputLabel>
                      <Select
                        label="District"
                        value={row.committeeDistrict || ""}
                        disabled={!canUseCommitteeFilters}
                        onChange={(e) =>
                          handleCommitteeDistrictChange(index, e.target.value)
                        }
                      >
                        {(row.committeeDistricts || []).map((d) => (
                          <MenuItem key={getOptionId(d)} value={getOptionId(d)}>
                            {getDistrictLabel(d)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}

                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={
                      !row.council ||
                      (row.committeeStates?.length > 0 && !row.committeeState) ||
                      (row.councilType === "REC" && !row.committeeRegion) ||
                      (row.councilType === "DEC" &&
                        (!row.committeeRegion || !row.committeeDistrict))
                    }
                  >
                    <InputLabel>Committee</InputLabel>
                    <Select
                      label="Committee"
                      value={
                        row.councilType === "DEC"
                          ? row.committeeName || ""
                          : row.committee
                      }
                      disabled={!canEditCommitteeDetails || !canUseCommitteeFilters}
                      onChange={(e) =>
                        handleCommitteeChange(index, e.target.value)
                      }
                    >
                      {row.committees.length === 0 && (
                        <MenuItem value="" disabled>
                          {getCommitteeEmptyMessage(row)}
                        </MenuItem>
                      )}
                      {row.committees.map((c) => (
                        <MenuItem
                          key={c._id}
                          value={row.councilType === "DEC" ? c.name : c._id}
                        >
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {row.committees.length === 0 && (
                      <FormHelperText>{getCommitteeEmptyMessage(row)}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {row.councilType === "DEC" &&
                row.committeeName &&
                (row.committeeSubtypes || []).length > 0 ? (
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!row.committeeName}
                    >
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={row.committeeSubtype || ""}
                        disabled={!canUseCommitteeFilters}
                        onChange={(e) =>
                          handleCommitteeSubtypeChange(index, e.target.value)
                        }
                      >
                        {(row.committeeSubtypes || []).map((subtype) => (
                          <MenuItem key={subtype.value} value={subtype.value}>
                            {subtype.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small" disabled={!row.committee}>
                    <InputLabel>Designations</InputLabel>
                    <Select
                      multiple
                      value={row.designations}
                      label="Designations"
                      disabled={!canEditCommitteeRole}
                      onChange={(e) =>
                        handleDesignationChange(index, e.target.value)
                      }
                      sx={{
                        "& .MuiSelect-select": {
                          whiteSpace: "normal",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                        },
                      }}
                      renderValue={(selectedIds) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {selectedIds.map((id) => {
                            const item = row.designationOptions.find(
                              (d) => d._id === id,
                            );

                            return (
                              <Chip
                                key={id}
                                label={item?.title || id}
                                size="small"
                                disabled={false}
                                sx={{
                                  maxWidth: "100%",
                                  height: "auto",
                                  ".MuiChip-label": {
                                    display: "block",
                                    whiteSpace: "normal",
                                  },
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onDelete={
                                  mode === "view" || !canEditCommitteeRole
                                    ? undefined
                                    : (e) => {
                                        e.stopPropagation();
                                        const updated = row.designations.filter(
                                          (d) => d !== id,
                                        );
                                        handleDesignationChange(index, updated);
                                      }
                                }
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {row.designationOptions.map((d) => (
                        <MenuItem key={d._id} value={d._id}>
                          {d.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {row.designations.map((designationId) => {
                  const designation = row.designationOptions.find(
                    (d) => d._id === designationId,
                  );
                  const rawScope =
                    row.designationScopes?.[designationId] ?? emptyScope;
                  const scope = {
                    ...emptyScope,
                    ...rawScope,
                    level: rawScope?.level || "NATIONAL",
                  };
                  const regionOptions =
                    scope.state && Array.isArray(scopeRegionsByState?.[scope.state])
                      ? scopeRegionsByState[scope.state]
                      : regions;
                  const districtOptions =
                    scope.state && Array.isArray(scopeDistrictsByState?.[scope.state])
                      ? scopeDistrictsByState[scope.state]
                      : districts;

                  return (
                    <div
                      style={{
                        width: "100%",
                        padding: "10px",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mt: 5,
                          mb: 2,
                          pb: 1,
                          borderBottom: "2px solid #eee",
                        }}
                      >
                        Scope for <b>{designation?.title}</b>
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Level</InputLabel>
                            <Select
                              label="Level"
                              value={scope.level || ""}
                            disabled={!canEditCommitteeRole}
                              onChange={(e) =>
                                handleScopeChange(
                                  index,
                                  designationId,
                                  "level",
                                  e.target.value,
                                )
                              }
                            >
                              {scopeLevels.map((l) => (
                                <MenuItem key={l.value} value={l.value}>
                                  {l.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {["STATE", "REGION", "DISTRICT"].includes(
                          scope.level,
                        ) && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>State</InputLabel>
                              <Select
                                label="State"
                                value={scope.state || ""}
                                disabled={!scope.level || !canEditCommitteeRole}
                                onChange={(e) =>
                                  handleScopeChange(
                                    index,
                                    designationId,
                                    "state",
                                    e.target.value,
                                  )
                                }
                              >
                                {states.map((s) => (
                                  <MenuItem key={getOptionId(s)} value={getOptionId(s)}>
                                    {getStateLabel(s)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {["REGION", "DISTRICT"].includes(scope.level) && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Region</InputLabel>
                              <Select
                                label="Region"
                                value={scope.region}
                                disabled={!scope.state || !canEditCommitteeRole}
                                onChange={(e) =>
                                  handleScopeChange(
                                    index,
                                    designationId,
                                    "region",
                                    e.target.value,
                                  )
                                }
                              >
                                {(regionOptions || []).map((r) => (
                                  <MenuItem key={getOptionId(r)} value={getOptionId(r)}>
                                    {getRegionLabel(r)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {scope.level === "DISTRICT" && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>District</InputLabel>
                              <Select
                                label="District"
                                value={scope.district}
                                disabled={!scope.state || !scope.region || !canEditCommitteeRole}
                                onChange={(e) =>
                                  handleScopeChange(
                                    index,
                                    designationId,
                                    "district",
                                    e.target.value,
                                  )
                                }
                              >
                                {(districtOptions || []).map((d) => (
                                  <MenuItem key={getOptionId(d)} value={getOptionId(d)}>
                                    {getDistrictLabel(d)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {scope.level === "CHAPTER" && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Chapter</InputLabel>
                              <Select
                                label="Chapter"
                                value={scope.chapter || ""}
                                disabled={!canEditCommitteeRole}
                                onChange={(e) =>
                                  handleScopeChange(
                                    index,
                                    designationId,
                                    "chapter",
                                    e.target.value,
                                  )
                                }
                              >
                                {chapters.map((c) => (
                                  <MenuItem key={c._id} value={c._id}>
                                    {c.chapterName || c.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {scope.level === "PROGRAM" && (
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Program</InputLabel>
                              <Select
                                label="Program"
                                value={scope.program || ""}
                                disabled={!canEditCommitteeRole}
                                onChange={(e) =>
                                  handleScopeChange(
                                    index,
                                    designationId,
                                    "program",
                                    e.target.value,
                                  )
                                }
                              >
                                {programs.map((p) => (
                                  <MenuItem key={p._id} value={p._id}>
                                    {p.programName || p.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  );
                })}
              </>
            )}

          </Grid>
        </Box>
      ))}

      {mode !== "view" &&
        authorityType.includes("COMMITTEE") &&
        canEditCommitteeDetails && (
        <Button
          sx={{ m: 2 }}
          variant="outlined"
          onClick={handleAddCommitteeRow}
        >
          + Add Another Committee
        </Button>
      )}

      {authorityType.includes("CHAPTER") &&
        userExistingChapterAssignments.length > 0 && (
          <Box sx={{ width: "100%", mb: 4 }}>
            <ReactTable
              columns={existingAssignmentColumns}
              rows={existingAssignmentRows}
              rowKey="chapterId"
              initialRowsPerPage={5}
              headerColor="linear-gradient(to right, #455a64, #78909c)"
              headerTextColor="#fff"
            />
          </Box>
        )}

      {authorityType.includes("CHAPTER") && addChapterAssignments && (
        <Box sx={{ width: "100%", mb: 4 }}>
          <Spinner loading={loading} />
          <ReactTable
            columns={testColumns}
            rows={chapterRows}
            rowKey="chapterName"
            initialRowsPerPage={5}
            headerColor="linear-gradient(to right, #004ecb, #00c6ff)"
            headerTextColor="#fff"
          />
        </Box>
      )}

      {authorityType.includes("CHAPTER") &&
        chapterRows.some((row) => row.isAssigned) && (
          <Box sx={{ width: "100%", mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Chapter Scope Details
            </Typography>
            {chapterRows
              .filter((row) => row.isAssigned)
              .map((row) => {
                const scope = row.scope || {};
                const regionOptions =
                  scope.state && Array.isArray(scopeRegionsByState?.[scope.state])
                    ? scopeRegionsByState[scope.state]
                    : regions;
                const districtOptions =
                  scope.state && Array.isArray(scopeDistrictsByState?.[scope.state])
                    ? scopeDistrictsByState[scope.state]
                    : districts;

                return (
                  <Box
                    key={`chapter-scope-${row._id}`}
                    sx={{
                      mt: 1,
                      py: 1,
                      px: 1.5,
                      border: "1px solid #ececec",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {row.chapterName}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Level</InputLabel>
                          <Select
                            label="Level"
                            value={scope.level || ""}
                            disabled={!isEditable}
                            onChange={(e) =>
                              handleChapterScopeChange(
                                row._id,
                                "level",
                                e.target.value,
                              )
                            }
                          >
                            {scopeLevels.map((l) => (
                              <MenuItem key={l.value} value={l.value}>
                                {l.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {["STATE", "REGION", "DISTRICT"].includes(scope.level) && (
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>State</InputLabel>
                            <Select
                              label="State"
                              value={scope.state || ""}
                              disabled={!isEditable || !scope.level}
                              onChange={(e) =>
                                handleChapterScopeChange(
                                  row._id,
                                  "state",
                                  e.target.value,
                                )
                              }
                            >
                              {states.map((s) => (
                                <MenuItem key={getOptionId(s)} value={getOptionId(s)}>
                                  {getStateLabel(s)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {["REGION", "DISTRICT"].includes(scope.level) && (
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Region</InputLabel>
                            <Select
                              label="Region"
                              value={scope.region || ""}
                              disabled={!isEditable || !scope.state}
                              onChange={(e) =>
                                handleChapterScopeChange(
                                  row._id,
                                  "region",
                                  e.target.value,
                                )
                              }
                            >
                              {(regionOptions || []).map((r) => (
                                <MenuItem key={getOptionId(r)} value={getOptionId(r)}>
                                  {getRegionLabel(r)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {scope.level === "DISTRICT" && (
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>District</InputLabel>
                            <Select
                              label="District"
                              value={scope.district || ""}
                              disabled={!isEditable || !scope.state || !scope.region}
                              onChange={(e) =>
                                handleChapterScopeChange(
                                  row._id,
                                  "district",
                                  e.target.value,
                                )
                              }
                            >
                              {(districtOptions || []).map((d) => (
                                <MenuItem key={getOptionId(d)} value={getOptionId(d)}>
                                  {getDistrictLabel(d)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })}
          </Box>
        )}

      {authorityType.includes("CHAPTER") && mode !== "view" && (
        <Button
          sx={{ mt: 1, mb: 2 }}
          variant="outlined"
          onClick={handleAddChapterAssignments}
        >
          + Add Chapter Assignments
        </Button>
      )}

      {authorityType.includes("PROGRAM") && (
        <Box sx={{ width: "100%", mt: 2 }}>
          {programRows.map((row, index) => (
            <Grid container spacing={2} key={`program-row-${index}`} sx={{ mb: 1 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Program Basket</InputLabel>
                  <Select
                    label="Program Basket"
                    value={row.basketId || ""}
                    disabled={!isEditable}
                    onChange={(e) => handleProgramBasketChange(index, e.target.value)}
                  >
                    {programBaskets.map((b) => (
                      <MenuItem key={b._id || b.basketId} value={b.basketId || b._id}>
                        {/* {b.basketId ? `${b.basketId} - ` : ""} */}
                        {b.basketName || b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Program (Optional)</InputLabel>
                  <Select
                    label="Program (Optional)"
                    value={row.programId || ""}
                    disabled={mode === "view" || !row.basketId}
                    onChange={(e) =>
                      handleProgramSelectionChange(index, e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(row.availablePrograms || []).map((p) => (
                      <MenuItem key={p._id} value={p._id}>
                        {p.programName || p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Designation</InputLabel>
                  <Select label="Designation" value="HEAD" disabled>
                    <MenuItem value="HEAD">Head</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={row.scope?.level || ""}
                    disabled={!isEditable}
                    onChange={(e) =>
                      handleProgramScopeChange(index, "level", e.target.value)
                    }
                  >
                    {scopeLevels.map((l) => (
                      <MenuItem key={l.value} value={l.value}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {["STATE", "REGION", "DISTRICT"].includes(row.scope?.level) && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State</InputLabel>
                    <Select
                      label="State"
                      value={row.scope?.state || ""}
                      disabled={!isEditable || !row.scope?.level}
                      onChange={(e) =>
                        handleProgramScopeChange(index, "state", e.target.value)
                      }
                    >
                      {states.map((s) => (
                        <MenuItem key={getOptionId(s)} value={getOptionId(s)}>
                          {getStateLabel(s)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {["REGION", "DISTRICT"].includes(row.scope?.level) && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Region</InputLabel>
                    <Select
                      label="Region"
                      value={row.scope?.region || ""}
                      disabled={!row.scope?.state || mode === "view"}
                      onChange={(e) =>
                        handleProgramScopeChange(index, "region", e.target.value)
                      }
                    >
                      {(row.scope?.state
                        ? scopeRegionsByState[row.scope.state]
                        : regions
                      )?.map((r) => (
                        <MenuItem key={getOptionId(r)} value={getOptionId(r)}>
                          {getRegionLabel(r)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {row.scope?.level === "DISTRICT" && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>District</InputLabel>
                    <Select
                      label="District"
                      value={row.scope?.district || ""}
                      disabled={!row.scope?.state || !row.scope?.region || mode === "view"}
                      onChange={(e) =>
                        handleProgramScopeChange(index, "district", e.target.value)
                      }
                    >
                      {(row.scope?.state
                        ? scopeDistrictsByState[row.scope.state]
                        : districts
                      )?.map((d) => (
                        <MenuItem key={getOptionId(d)} value={getOptionId(d)}>
                          {getDistrictLabel(d)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "center" }}>
                {mode !== "view" && (
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={() => handleRemoveProgramRow(index)}
                  >
                    Remove
                  </Button>
                )}
              </Grid>
            </Grid>
          ))}

          {mode !== "view" && (
            <Button variant="outlined" onClick={handleAddProgramRow}>
              + Add Program Assignment
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AssignmentsTab;
